/**
 * The phone list.
 *
 * Under 768px the table becomes cards, because a table that scrolls sideways on a phone is
 * how a packer marks the wrong order packed. Same rows, same server chosen action, same
 * drawer: only the arrangement changes.
 *
 * Line one is the order number and the total. Line two is the customer and where it is
 * going. Line three is the payment state and the fulfilment state. The one contextual
 * button sits at the end, full height, so it is a real 44px target.
 */
import { Button, EmptyState, Pill } from '@/components';
import { countdown, formatCount, formatMoney, relativeAge } from '@/lib/format';
import {
  MATCHER_LABEL,
  PAYMENT_GLYPH,
  PAYMENT_STATE_LABEL,
  PAYMENT_STATE_TONE,
  QUICK_ACTION_LABEL,
  ATTENTION_LABEL,
  type OrdersPageRowView as OrdersPageRow,
} from './types';
import type { RowHandlers } from './cells';

export interface OrderCardListProps {
  rows: readonly OrdersPageRow[];
  onOpen: (row: OrdersPageRow) => void;
  handlers: RowHandlers;
  emptyMessage: string;
}

export function OrderCardList({ rows, onOpen, handlers, emptyMessage }: OrderCardListProps) {
  if (rows.length === 0) return <EmptyState message={emptyMessage} />;

  return (
    <ul className="ord-cards" role="list" aria-label="Orders">
      {rows.map((row) => (
        <li key={row.order_id} className="ord-card" role="listitem">
          <button
            type="button"
            className="ord-card__body"
            onClick={() => onOpen(row)}
            aria-label={`Open order ${row.number}`}
          >
            <span className="ord-card__top">
              <span className="ord-num">{row.number}</span>
              {row.total_cents !== undefined ? (
                <span className="ord-total">{formatMoney(row.total_cents)}</span>
              ) : null}
            </span>

            <span className="ord-card__line">
              <span className="ord-name">{row.customer.name}</span>
              <span className="ord-sub">
                {[row.customer.city, row.customer.state].filter(Boolean).join(', ')}
              </span>
            </span>

            <span className="ord-card__line ord-card__pills">
              <Pill tone={PAYMENT_STATE_TONE[row.payment.state]}>
                {row.payment.method ? `${PAYMENT_GLYPH[row.payment.method]} ` : ''}
                {PAYMENT_STATE_LABEL[row.payment.state]}
              </Pill>
              {row.payment.matcher ? (
                <span className="ord-badge ord-badge--matcher">
                  {MATCHER_LABEL[row.payment.matcher]}
                </span>
              ) : null}
              {row.flags.first_order ? (
                <span className="ord-badge ord-badge--first">FIRST</span>
              ) : null}
              <span className="ord-sub">
                {relativeAge(row.placed_at, handlers.now)} . {formatCount(row.item_count)} items
              </span>
            </span>

            {row.payment.state === 'waiting' && row.payment.autocancel_at ? (
              <span
                className="ord-sub ord-countdown"
                data-late={Date.parse(row.payment.autocancel_at) <= handlers.now ? 'true' : 'false'}
              >
                {Date.parse(row.payment.autocancel_at) <= handlers.now
                  ? `overdue ${countdown(row.payment.autocancel_at, handlers.now)}`
                  : `cancels in ${countdown(row.payment.autocancel_at, handlers.now)}`}
              </span>
            ) : null}

            {row.attention_reasons.length > 0 ? (
              <span className="ord-reasons">
                {row.attention_reasons.map((reason) => (
                  <span key={reason} className="ord-reason">
                    {ATTENTION_LABEL[reason]}
                  </span>
                ))}
              </span>
            ) : null}
          </button>

          <div className="ord-card__action">
            <Button
              size="sm"
              variant={
                row.quick_action === 'mark_paid' || row.quick_action === 'print'
                  ? 'primary'
                  : 'default'
              }
              busy={handlers.isActing(row.order_id)}
              onClick={() => handlers.onQuickAction(row)}
            >
              {QUICK_ACTION_LABEL[row.quick_action]}
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}
